import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { UserService } from '../../../service/user.service';
import { TokenIdService } from '../../../service/token-id.service';
import { AedNumberPipe } from '../../../shared/pipes/aed-number.pipe';


interface Portfolio {
  id: number;
  name: string;
  investment: number;
  currentValue: number;
  roi: number;
  status: string;
  profit:any
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  investmentGoals: string;
  lastLogin?: string;
}

interface DashboardData {
  totalInvestment: number;
  currentValue: number;
  totalProfit: number;
  joinDate: string;
  portfolios: Portfolio[];
}

interface PerformanceMetric {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
}

interface AccountAlert {
  type: 'success' | 'warning' | 'info';
  title: string;
  message: string;
  time: string;
}

interface UpcomingPayment {
  portfolio: string;
  amount: number;
  date: string;
  type: string;
  status: string;
}

@Component({
  selector: 'app-overview-tab',
  imports: [CommonModule, LucideIconsModule, AedNumberPipe],
  templateUrl: './overview-tab.component.html',
  styleUrl: './overview-tab.component.css'
})
export class OverviewTabComponent implements OnInit {
@Input() user!: User;

  dashboardData: DashboardData = {
    totalInvestment: 0,
    currentValue: 0,
    totalProfit: 0,
    joinDate: '',
    portfolios: []
  };

  performanceMetrics: PerformanceMetric[] = [];

  accountAlerts: AccountAlert[] = [];

  upcomingPayments: UpcomingPayment[] = [];

  portfolioIcons: { [key: string]: string } = {
    'Real Estate Portfolio': 'building-2',
    'Clean Energy (LNG)': 'zap',
    'AI Portfolio': 'cpu',
    'Agriculture Fund': 'wheat'
  };

  formatNumber(value: number): string {
    return value.toLocaleString();
  }

  getProfitLoss(portfolio: Portfolio): number {
    return portfolio.currentValue - portfolio.investment;
  }

  getPortfolioIcon(portfolioName: string): string {
    return this.portfolioIcons[portfolioName] || 'circle';
  }

  getMetricIcon(trend: string): string {
    switch (trend) {
      case 'up': return 'arrow-up-right';
      case 'down': return 'arrow-down-right';
      default: return 'activity';
    }
  }

  getMetricTrendClass(trend: string): string {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  }

  // Empty state helpers
  get hasPerformanceMetrics(): boolean {
    return Array.isArray(this.performanceMetrics) && this.performanceMetrics.length > 0;
    }

  get hasPortfolios(): boolean {
    return Array.isArray(this.dashboardData?.portfolios) && this.dashboardData.portfolios.length > 0;
  }

  get hasAlerts(): boolean {
    return Array.isArray(this.accountAlerts) && this.accountAlerts.length > 0;
  }

  getAlertIcon(type: string): string {
    switch (type) {
      case 'success': return 'check-circle';
      case 'warning': return 'circle-alert';
      default: return 'bell';
    }
  }

  getAlertClass(type: string): string {
    switch (type) {
      case 'success': return 'bg-green-50 border-green-500';
      case 'warning': return 'bg-yellow-50 border-yellow-500';
      default: return 'bg-blue-50 border-blue-500';
    }
  }

  getAlertIconClass(type: string): string {
    switch (type) {
      case 'success': return 'text-green-500';
      case 'warning': return 'text-yellow-500';
      default: return 'text-blue-500';
    }
  }

  getPaymentStatusClass(status: string): string {
    return status === 'Scheduled' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  }

  constructor(private userService: UserService, private tokenIdService: TokenIdService) {}

  ngOnInit(): void {
    const token = this.userService.getAuthToken();
    const userId = this.tokenIdService.getUserIdFromToken(token || '');
    if (!userId) {
      return;
    }
    this.userService.getDashboard(userId).subscribe({
      next: (res: any) => {
        // Expecting shape: { success, data: { summary, performanceMetrics, activePortfolios, accountAlerts } }
        const data = res?.data || {};
        const summary = data.summary || {};
        const activePortfolios = Array.isArray(data.activePortfolios) ? data.activePortfolios : [];
        const metrics = data.performanceMetrics || {};

        // Map to existing UI model (no UI changes)
        this.dashboardData = {
          totalInvestment: Number(summary.totalInvestment) || 0,
          currentValue: Number(summary.totalCurrentValue) || 0,
          totalProfit: Number(summary.totalProfit) || 0,
          joinDate: summary.firstInvestmentDate || '',
          portfolios: activePortfolios
          .map((p: any, idx: number) => ({
            id: idx + 1,
            name: p.name,
            investment: Number(p.investedAmount) || 0,
            currentValue: Number(p.currentValue) || 0,
            roi: Number(p.roiPercentage) || 0,
            profit: Number(p.profit) || 0,
            status: p.status || 'Active'
          }))
        };

        this.performanceMetrics = [
          { label: 'Total ROI', value: `${Number(metrics.totalRoi ?? summary.totalRoiPercentage ?? 0)}%`, change: 'Stable', trend: 'neutral' },
          { label: 'Risk Score', value: String(metrics.riskScore ?? 'N/A'), change: 'Stable', trend: 'neutral' },
          { label: 'Diversification', value: String(metrics.diversification ?? (summary.diversified ? 'Yes' : 'No')), change: 'Stable', trend: 'neutral' },
          { label: 'Active Portfolios', value: String(summary.activePortfoliosCount ?? this.dashboardData.portfolios.length), change: 'Stable', trend: 'neutral' },
        ];

        // If backend provides alerts, map them; else, keep empty
        const pending = data.accountAlerts?.pendingTransactions || [];
        this.accountAlerts = pending.map((t: any) => ({
          type: 'info',
          title: 'Pending Transaction',
          message: `${t.type || 'Transaction'} of ${t.amount || ''} ${t.currency || ''} is pending`,
          time: t.date || ''
        }));

        // Upcoming payments (if provided by backend)
        const upcoming = Array.isArray(data.upcomingPayments) ? data.upcomingPayments : [];
        this.upcomingPayments = upcoming.map((p: any) => ({
          portfolio: p.portfolio || p.name || '',
          amount: Number(p.amount) || 0,
          date: p.date || '',
          type: p.type || 'Payment',
          status: p.status || 'Scheduled'
        }));
      },
      error: () => {
        // keep defaults on error
      }
    });
  }
}
